import { useUserEventLogger } from '@repo/tracking-service';
import { BoxButton, Description, Divider, StickyBottomWrapper, Svg, Table, TableCell, useDialog, useToast } from '@repo/ui';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useMutation, useRelayEnvironment } from 'react-relay';

import { revokeCancelMutation } from '@/__generated__/revokeCancelMutation.graphql';
import { checkRefund } from '@/components/tax-refund/common/graphql/query/checkRefund';
import ApplyResultFaqView from '@/components/tax-refund/common/view/ApplyResultFaqView';
import ApplyResultInfoView from '@/components/tax-refund/common/view/ApplyResultInfoView';
import { revokeCancel } from '@/graphql/mutation/taxRefund/revokeCancel';
import { getCorporationDefaultInfo, getIndividualDefaultInfo } from '@/lib/constants/info-contents';
import { COMMON_PATHS } from '@/lib/constants/paths';
import { ApplyResultData } from '@/lib/types/request';
import { formatAccountBankNo } from '@/lib/utils/stringFormat';

export const ApplyResultReceiveCancelContent = ({ data }: { data: ApplyResultData }) => {
  const isCorps = data.businessType === 'corps';
  const displayName = isCorps ? (data.fullName ?? data.name) : data.name;
  const amountLabel = isCorps ? '법인세 예상 환급액' : '종합소득세 예상 환급액';
  const faqClickEvent = isCorps ? 'corps-result_faq_cancel-receipt' : 'indis-result_faq_cancel-receipt';
  const restartClickEvent = isCorps ? 'corps-result_restart-refund' : 'indis-result_restart-refund';
  const restartDialogEvent = `${isCorps ? 'corps' : 'indis'}-result_restart-refund_dialog`;
  const restartDialogRetryEvent = `${isCorps ? 'corps' : 'indis'}-result_restart-refund_dialog-retry`;
  const restartDialogCloseEvent = `${isCorps ? 'corps' : 'indis'}-result_restart-refund_dialog-close`;
  const restartReStartedDialogEvent = `${isCorps ? 'corps' : 'indis'}-result_restart-refund_restarted-dialog`;
  const restartReStartedDialogOkEvent = `${isCorps ? 'corps' : 'indis'}-result_restart-refund_restarted-dialog-ok`;
  const refreshDialogEvent = `${isCorps ? 'corps' : 'indis'}-result_restart-refund_refresh-dialog`;
  const refreshDialogOkEvent = `${isCorps ? 'corps' : 'indis'}-result_restart-refund_refresh-dialog-ok`;

  const { sendClickEvent, sendViewEvent } = useUserEventLogger();
  const { showDialog } = useDialog();
  const { toast } = useToast();
  const relayEnvironment = useRelayEnvironment();
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [handleRevokeCancel] = useMutation<revokeCancelMutation>(revokeCancel);

  const router = useRouter();
  const { hasRegisteredCard, cardName, cardLogo, cardNumber } = data.paymentCard;
  const infoContents = isCorps ? getCorporationDefaultInfo() : getIndividualDefaultInfo(hasRegisteredCard ?? false);

  const handleCardClick = () => {
    if (isCorps) return;
    router.push(COMMON_PATHS.환급결제카드입력);
  };

  const restartRefund = () => {
    handleRevokeCancel({
      variables: { refundId: data.refundId },
      onCompleted: (response) => {
        if (response.revokeCancel.result) {
          sendViewEvent(restartReStartedDialogEvent, undefined, { isAllowRetry: true });
          showDialog({
            title: '이제 환급이 다시 진행돼요',
            description: '신속하고 빠짐없이 환급받으실 수 있도록 최선을 다할게요.',
            buttons: {
              primary: {
                onClick: () => {
                  sendClickEvent(restartReStartedDialogOkEvent);
                  window.location.reload();
                }
              }
            }
          });
        } else {
          setButtonDisabled(false);
          toast({ title: '일시적인 오류가 발생했어요. 잠시 후 다시 시도해주세요.', variant: 'warning' });
        }
      },
      onError: () => {
        setButtonDisabled(false);
        toast({ title: '일시적인 오류가 발생했어요. 잠시 후 다시 시도해주세요.', variant: 'warning' });
      }
    });
  };

  const handleRestartClick = async () => {
    sendClickEvent(restartClickEvent);
    setButtonDisabled(true);

    const refund = await checkRefund(relayEnvironment, data.refundId);
    if (
      refund?.cancelStage === 'Canceling' &&
      refund?.refundStatus === 'ApplyComplete' &&
      (refund?.refundStage === 'Review' || refund?.refundStage === 'Declare')
    ) {
      sendViewEvent(restartDialogEvent, undefined, { isAllowRetry: true });
      showDialog({
        title: '환급을 다시 진행할까요?',
        description: '재조회 없이 진행했던 단계부터 다시 환급 진행이 가능해요.',
        buttons: {
          primary: {
            text: '재진행',
            onClick: () => {
              sendClickEvent(restartDialogRetryEvent);
              restartRefund();
            }
          },
          secondary: {
            onClick: () => {
              setButtonDisabled(false);
              sendClickEvent(restartDialogCloseEvent);
            }
          }
        }
      });
      return;
    }

    sendViewEvent(refreshDialogEvent, undefined, { isAllowRetry: true });
    showDialog({
      title: '진행 단계에 변동이 있어 새로고침이 필요해요',
      description: '확인을 누르시면 현재 상태로 페이지가 새로고침돼요.',
      buttons: {
        primary: {
          text: '확인',
          onClick: () => {
            setButtonDisabled(false);
            sendClickEvent(refreshDialogOkEvent);
            window.location.reload();
          }
        }
      }
    });
  };

  return (
    <div>
      <div className={'px-4.5 py-normal-small'}>
        <Description
          title={isCorps ? `’${displayName}’의 환급 취소 신청이 접수됐어요` : `${displayName}님의\n환급 취소 신청이 접수됐어요`}
          size={'medium'}
          description={'영업일 기준 3일 내에 취소 완료돼요.\n취소 접수로 제휴 세무사 검토는 중단돼요.'}
          graphic={<Svg src="icg_document-minus_md.svg" width={80} height={80} />}
        />
      </div>
      <Divider size={'large'} />
      <div className={'px-4.5 py-tiny-large'}>
        <div className={'pb-normal-large'}>
          <div className={'body-large-regular text-neutral-foreground-low pt-normal-large'}>{amountLabel}</div>
          <div className={'text-neutral-foreground-main heading-medium-bold'}>{data.refundAmount}</div>
          <Divider className={'my-normal-medium'} />
          <Table
            headerCell={<TableCell variant={'header'}>환급 신청일</TableCell>}
            contentCell={<TableCell className={'flex-1 text-right'}>{data.applyDate}</TableCell>}
          />
          <Table
            headerCell={<TableCell variant={'header'}>예상 환급일</TableCell>}
            contentCell={<TableCell className={'flex-1 text-right'}>신청일부터 2~3개월</TableCell>}
          />
          {data.applicationInfo && (
            <Table
              headerCell={<TableCell variant={'header'}>환급 계좌</TableCell>}
              contentCell={
                <TableCell className={'flex-1 text-right'}>{formatAccountBankNo(data.applicationInfo?.bank, data.applicationInfo?.accountNo)}</TableCell>
              }
            />
          )}
          <Table
            headerCell={<TableCell variant={'header'}>이용료</TableCell>}
            contentCell={
              <TableCell className={'flex-1 text-right'}>
                <div>
                  <p className={'mb-1'}>결정 환급액의 {data.defaultFeeRate}%</p>
                  <p className={'text-13 text-gray-500'}>(부가세 별도)</p>
                </div>
              </TableCell>
            }
          />
          {data.discountRate !== '0' && (
            <Table
              headerCell={<TableCell variant={'header'}>이용료 할인</TableCell>}
              contentCell={
                <TableCell className={'flex-1 text-right'}>
                  <div>
                    <p className={'mb-1'}>제휴 할인 {data.discountRate}% 적용</p>
                    <p className={'text-13 text-gray-500'}>
                      결정 환급액의 <span className={'line-through'}>{data.defaultFeeRate}%</span>-&gt;{data.feeRate}% (vat별도)
                    </p>
                  </div>
                </TableCell>
              }
            />
          )}
          {!isCorps && hasRegisteredCard && cardNumber && (
            <Table
              headerCell={<TableCell variant={'header'}>결제 카드</TableCell>}
              contentCell={
                <>
                  <TableCell className={'flex items-center flex-1 justify-end'}>
                    <div className={'flex gap-tiny-medium text-[15px]'}>
                      <Svg src={cardLogo} width={24} height={24} />
                      {cardName}({cardNumber.slice(-4)})
                    </div>
                  </TableCell>
                  <TableCell className={'flex-1'}>
                    <BoxButton size={'small'} variant={'tonal-secondary'} onClick={handleCardClick}>
                      변경
                    </BoxButton>
                  </TableCell>
                </>
              }
            />
          )}
        </div>
        <ApplyResultInfoView data={infoContents} />
      </div>
      <Divider size={'large'} />
      <ApplyResultFaqView data={faqContents} clickEventName={faqClickEvent} />
      <div className={'pb-normal-medium'} />
      <StickyBottomWrapper>
        <BoxButton className={'w-full'} size={'large'} variant={'tonal-primary'} onClick={handleRestartClick} disabled={buttonDisabled}>
          다시 환급 진행하기
        </BoxButton>
      </StickyBottomWrapper>
    </div>
  );
};

const faqContents = [
  {
    title: '취소는 언제 완료되나요?',
    content: '취소 접수 시 영업일 기준 3일 내에 취소가 완료돼요.'
  },
  {
    title: '취소해도 이용료가 발생하나요?',
    content:
      '신청 후 24시간 내에 취소 접수하면 별도의 이용료가 발생하지 않아요.\n취소 접수와 함께 검토는 중단되기 때문에, 24시간 내에 취소 접수를 하는 경우 취소 완료 전에는 환급 진행이 되지 않으며 이용료도 발생하지 않아요.'
  },
  {
    title: '취소 후에도 다시 환급 신청할 수 있나요?',
    content:
      '비즈넵 환급에서는 취소 완료 후에도 언제든지 다시 신청할 수 있어요.\n취소 접수 후 취소 완료가 되기 전에, 취소를 철회하고 이어서 환급을 진행하길 원하신다면 아래 [다시 환급 진행하기]를 눌러 재진행하시거나 고객센터로 문의해주세요.'
  },
  {
    title: '취소 완료되면 알림이 오나요?',
    content:
      '취소가 완료되면 카카오톡 알림톡으로 알려드려요.\n취소 완료가 되기 전에, 취소를 철회하고 이어서 환급을 진행하길 원하신다면 아래 [다시 환급 진행하기]를 눌러 재진행하시거나 고객센터로 문의해주세요.'
  },
  {
    title: '취소 접수 후 완료를 기다리는 동안 이용료가 발생하지는 않나요?',
    content:
      '환급 신청 후 24시간 내에 취소 접수를 하셨다면 별도의 이용료가 발생하지 않아요.\n제휴 세무사의 검토가 시작된 이후 취소 접수를 하셨다면 지금까지의 업무 진행비가 요청될 수 있어요. 취소 접수 시 환급 검토는 중단되고, 이때 업무 진행비는 취소 접수일 기준으로 안내드려요.'
  },
  {
    title: '검토 진행된 경우, 왜 업무 진행비가 발생하나요?',
    content:
      '경정청구 신고는 다른 신고 유형과는 다르게 준비해야 할 자료가 많고 계산 방식이 까다로워요. 고객님의 환급금을 찾아드리기 위해 제휴 세무사가 검토하고 신고를 준비했기 때문에 업무 진행비가 발생할 수 있어요.\n이미 검토가 진행됐다면 업무 진행비를 지불하고 취소하기보다 비즈넵 환급에서 계속 환급 과정을 진행해 환급금을 찾으시길 추천드려요.'
  }
];
